import { useQueryClient } from "@tanstack/react-query";
import { api, getSession, refreshSession } from "@wirevod/app-core";
import { useToast } from "@wirevod/ui";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react";
import { keys } from "@/api/keys";
import { type UploadItem, UploadManager } from "./manager";
import { UploadPanel } from "./UploadPanel";

const Context = createContext<UploadManager | null>(null);

async function currentToken(): Promise<string> {
  if (!getSession()) await refreshSession();
  return getSession()?.accessToken ?? "";
}

/** Owns background uploads for the whole signed-in app (survives navigation). */
export function UploadProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [manager] = useState(
    () =>
      new UploadManager({
        getToken: currentToken,
        abortRemote: async (id) => {
          await api.DELETE("/v1/uploads/{id}", { params: { path: { id } } });
        },
        onComplete: (item) => {
          void queryClient.invalidateQueries({ queryKey: keys.assets.all });
          void queryClient.invalidateQueries({ queryKey: keys.overview });
          toast({
            title: `“${item.title}” uploaded`,
            description: "WireVoD is preparing it now.",
            tone: "success",
          });
        },
      }),
  );

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (manager.busy) event.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [manager]);

  return (
    <Context.Provider value={manager}>
      {children}
      <UploadPanel />
    </Context.Provider>
  );
}

export function useUploadManager(): UploadManager {
  const manager = useContext(Context);
  if (!manager) throw new Error("useUploadManager must be used inside <UploadProvider>");
  return manager;
}

export function useUploads(): UploadItem[] {
  const manager = useUploadManager();
  return useSyncExternalStore(manager.subscribe, manager.getSnapshot, manager.getSnapshot);
}
